import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  Appointment,
  AppointmentHistoryEntry,
  CreateScoutOsaRatingInput,
  ScoutAssignment,
  ScoutOsaRating,
} from '../types'

import { appointmentsApi } from './appointments.api'

import type { OsaEnvironment } from '@/shared/types'
import type { EmailItem } from '@/shared/types/email.types'

const APPOINTMENT_QUERY_KEY = 'appointments'

export const useAppointmentsQuery = (
  params?: {
    page?: number
    pageSize?: number
    status?: string | string[]
    sortField?: string
    sortOrder?: string
    env?: OsaEnvironment
    buildingUUID?: string
  },
  options?: Omit<UseQueryOptions<{ data: Appointment[]; total: number }>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [APPOINTMENT_QUERY_KEY, params],
    queryFn: () => appointmentsApi.getAppointments(params),
    ...options,
  })
}

export const useAppointmentQuery = (
  id: string,
  options?: Omit<UseQueryOptions<{ data: Appointment }>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [APPOINTMENT_QUERY_KEY, id],
    queryFn: () => appointmentsApi.getAppointmentById(id),
    enabled: !!id,
    ...options,
  })
}

export const useCreateOnSiteAppointmentMutation = (
  options?: UseMutationOptions<
    { data: Appointment },
    Error,
    {
      leadUUID: string
      firstName: string
      lastName: string
      email: string
      phone: string
      buildingUUID: string
      street: string
      city: string
      postalCode: string
      type: 'isfp' | 'marketplace'
      subtypes?: ('heatpump' | 'pv')[]
    }
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: data => appointmentsApi.createOnSiteAppointment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APPOINTMENT_QUERY_KEY] })
    },
    ...options,
  })
}

export const useRescheduleAppointmentMutation = (
  options?: UseMutationOptions<
    { data: Appointment },
    Error,
    {
      appointmentId: string
      scoutUUID: string
      scheduleStartTime: string
      type: string
    }
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ appointmentId, scoutUUID, scheduleStartTime, type }) =>
      appointmentsApi.rescheduleAppointment(appointmentId, { scoutUUID, scheduleStartTime, type }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APPOINTMENT_QUERY_KEY] })
    },
    ...options,
  })
}

export const useUpdateAppointmentMutation = (
  options?: UseMutationOptions<
    { data: Appointment },
    Error,
    { id: string; data: Partial<Appointment> }
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }) => appointmentsApi.updateAppointment(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [APPOINTMENT_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [APPOINTMENT_QUERY_KEY, variables.id] })
    },
    ...options,
  })
}

export const useScoutAssignmentsQuery = (
  appointmentId: string,
  options?: Omit<UseQueryOptions<{ data: ScoutAssignment[] }>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [APPOINTMENT_QUERY_KEY, 'scout-assignments', appointmentId],
    queryFn: () => appointmentsApi.getScoutAssignments(appointmentId),
    enabled: !!appointmentId,
    ...options,
  })
}

export const useRetriggerScoutNotificationsMutation = (
  options?: UseMutationOptions<{ code: number; errors: string[] }, Error, string>
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (appointmentId: string) =>
      appointmentsApi.retriggerScoutNotifications(appointmentId),
    onSuccess: (_, appointmentId) => {
      queryClient.invalidateQueries({ queryKey: [APPOINTMENT_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [APPOINTMENT_QUERY_KEY, appointmentId] })
      queryClient.invalidateQueries({
        queryKey: [APPOINTMENT_QUERY_KEY, 'scout-assignments', appointmentId],
      })
    },
    ...options,
  })
}

export const useAppointmentEmailsQuery = (
  appointmentId: string,
  options?: Omit<UseQueryOptions<{ data: EmailItem[] }>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [APPOINTMENT_QUERY_KEY, 'emails', appointmentId],
    queryFn: () => appointmentsApi.getAppointmentEmails(appointmentId),
    enabled: !!appointmentId,
    ...options,
  })
}

export const useAppointmentHistoryQuery = (
  appointmentId: string,
  options?: Omit<UseQueryOptions<{ data: AppointmentHistoryEntry[] }>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [APPOINTMENT_QUERY_KEY, 'history', appointmentId],
    queryFn: () => appointmentsApi.getAppointmentHistory(appointmentId),
    enabled: !!appointmentId,
    ...options,
  })
}

// Hook to check for active appointments for a specific building and appointment type
export const useCheckActiveAppointments = (
  buildingUUID?: string,
  type?: string,
  options?: Omit<UseQueryOptions<Appointment | null>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [APPOINTMENT_QUERY_KEY, 'active-check', buildingUUID, type],
    queryFn: async () => {
      const result = await appointmentsApi.getAppointments({
        buildingUUID,
        type,
        status: ['new', 'dispatched'],
        pageSize: 1,
      })
      return result.data.length > 0 ? result.data[0] : null
    },
    enabled: !!buildingUUID,
    ...options,
  })
}

export const useCreateScoutAssignmentMutation = (
  options?: UseMutationOptions<
    { data: ScoutAssignment },
    Error,
    { appointmentId: string; scoutUUID: string }
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ appointmentId, scoutUUID }) =>
      appointmentsApi.createScoutAssignment(appointmentId, scoutUUID),
    onSuccess: (_, variables) => {
      // Invalidate appointment and scout assignments queries
      queryClient.invalidateQueries({ queryKey: [APPOINTMENT_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [APPOINTMENT_QUERY_KEY, variables.appointmentId] })
      queryClient.invalidateQueries({
        queryKey: [APPOINTMENT_QUERY_KEY, 'scout-assignments', variables.appointmentId],
      })
    },
    ...options,
  })
}

export const useCreateScoutOsaRatingMutation = (
  options?: UseMutationOptions<
    void,
    Error,
    { appointmentId: string; data: CreateScoutOsaRatingInput }
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ appointmentId, data }) =>
      appointmentsApi.createScoutOsaRating(appointmentId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [APPOINTMENT_QUERY_KEY, 'scout-ratings', variables.appointmentId],
      })
    },
    ...options,
  })
}

export const useScoutOsaRatingsQuery = (
  appointmentId: string,
  options?: Omit<UseQueryOptions<{ data: ScoutOsaRating[] }>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [APPOINTMENT_QUERY_KEY, 'scout-ratings', appointmentId],
    queryFn: () => appointmentsApi.getScoutOsaRatings(appointmentId),
    enabled: !!appointmentId,
    ...options,
  })
}
